import { useNavigate } from 'react-router-dom';
import { Common } from '@thecvlb/design-system';
import classNames from 'classnames';

import SafeHtml from 'shared/SafeHtml';

import { PathName } from 'router/paths';
import { parseText } from 'utils/parseText';

import { DefaultMessageProps } from './defaultMessage.types';

type Props = DefaultMessageProps & {
  /**
   * Opt-in slot rendered below the text inside the bubble (the AI chat's Yes / No). Independent
   * of `message.buttons`, which stays the care-team appointment CTA.
   */
  actions?: React.ReactNode;
};

const DefaultMessage: React.FC<Props> = ({ actions, combined, message, self }) => {
  const navigate = useNavigate();
  const formattedMessage = parseText(message?.message, { markdown: !!message.renderMarkdown });

  return (
    <>
      {(message.message || message.isLoadingMessage || !!message.buttons.length || !!actions) && (
        <div
          className={classNames(
            'w-fit rounded-xl px-5 py-3',
            self ? 'ml-6 bg-secondary-100' : 'mr-6 bg-gray-100',
            !combined && (self ? 'rounded-br-none' : 'rounded-bl-none')
          )}
        >
          {message.isLoadingMessage ? (
            // The minimal typing state (Dominique, Sept 17): three dots, nothing else in the bubble.
            // Who is typing is said by the sender label above the bubble and by `loadingLabel`
            // for screen readers.
            <div
              aria-label={message.loadingLabel ?? 'Assistant is typing'}
              className="flex items-center gap-1.5 text-gray-500"
              role="status"
            >
              {[0, 1, 2].map((index) => (
                <span
                  className="size-2 animate-bounce rounded-full bg-current"
                  key={index}
                  style={{ animationDelay: `${index * 150}ms` }}
                />
              ))}
            </div>
          ) : (
            <div
              className={classNames('break-word break-words whitespace-normal text-gray-700', {
                '[&>*+*]:mt-3 [&_li+li]:mt-1 [&_p]:leading-6': message.renderMarkdown,
                'whitespace-pre-line': !message.renderMarkdown
              })}
            >
              <SafeHtml html={formattedMessage} />
            </div>
          )}
          {message.buttons.map(({ label }, index) => (
            <Common.Button
              className="mt-4"
              color="blue"
              key={index.toString()}
              preIcon="lifemd-reversed"
              style="pill"
              onClick={() => navigate(PathName.CreateAppointment)}
            >
              {label}
            </Common.Button>
          ))}
          {actions}
        </div>
      )}
    </>
  );
};

export default DefaultMessage;
